import { useCallback, useEffect, useRef, useState } from 'react'
import type { ProjectOption } from '@ai-meeting/shared'
import { fetchBacklogConnection } from '@/services/backlog'
import { fetchProjects } from '@/services/projects'
import { BACKLOG_CONNECTED_MESSAGE } from '@/utils/backlogConnectPopup'
import { copilotSetupNeed, type CopilotSetupNeed } from './copilotSetupState'

export interface CopilotBacklogSetup {
  /** What to put in front of the reader, or `none` to draw nothing. */
  need: CopilotSetupNeed
  /** What the reader's own grant can see, for the picker. */
  projects: ProjectOption[]
  /**
   * Whether the first answer is still outstanding.
   *
   * "Nothing has been read yet", not "a request is in flight". It goes false
   * on the first settled read and stays there, so the focus-driven re-reads
   * that follow do not blink the control the reader is looking at.
   */
  loading: boolean
  /** Read both answers again — after connecting, or when asked. */
  refresh: () => void
}

interface Options {
  /** False while the panel is shut, or on a meeting this cannot be used on. */
  enabled: boolean
  /**
   * Called with the Backlog account behind a working grant, on the first
   * reading that names one and whenever it changes afterwards.
   *
   * The caller is what decides whether the running session needs telling; this
   * only reports what it saw.
   */
  onAccountKnown: (accountId: number) => void
}

/**
 * The two things the Copilot needs before it can answer about tickets, read
 * ahead of the question rather than after it (BRAI-689).
 *
 * Re-read whenever the window regains focus, and as soon as the approval
 * window says it is done. Connecting happens in a window of its own while this
 * panel stays mounted behind it, so a single read on open would leave the
 * strip inviting the reader to connect an account they just connected — which
 * is the bug this was opened for.
 *
 * Silent on failure. The strip is an invitation, and Backlog being unreachable
 * for a moment is not a reason to put an error in a panel whose job is the
 * meeting; the refusal that comes back from an actual question still says what
 * went wrong.
 */
export function useCopilotBacklogSetup({
  enabled,
  onAccountKnown,
}: Options): CopilotBacklogSetup {
  const [need, setNeed] = useState<CopilotSetupNeed>('none')
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [loading, setLoading] = useState(true)
  const [reloads, setReloads] = useState(0)

  const refresh = useCallback(() => setReloads((count) => count + 1), [])

  // Held in a ref so a caller that rebuilds the callback each render does not
  // restart the reads, which would make focus-driven refreshes into a loop.
  const onAccountKnownRef = useRef(onAccountKnown)
  onAccountKnownRef.current = onAccountKnown
  const lastAccountRef = useRef<number | null | undefined>(undefined)

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    // Readings are ordered rather than merely cancelled: a focus event lands
    // while the first read is still out often enough, and the slower of the
    // two can answer last with the older truth.
    let latest = 0

    function read() {
      const request = ++latest
      void Promise.all([fetchBacklogConnection(), fetchProjects()])
        .then(([connection, projectList]) => {
          if (cancelled || request !== latest) return
          setProjects(projectList.projects)
          setNeed(
            copilotSetupNeed({
              status: connection.status,
              projectCount: projectList.projects.length,
            })
          )
          const accountId = connection.account?.id ?? null
          if (accountId !== null && accountId !== lastAccountRef.current) {
            onAccountKnownRef.current(accountId)
          }
          lastAccountRef.current = accountId
        })
        .catch(() => {
          // `none` draws nothing. An unreachable Backlog therefore leaves the
          // panel as the meeting rather than putting an error in it; the
          // refusal that comes back from an actual question still says what
          // went wrong.
          if (cancelled || request !== latest) return
          setNeed('none')
          setProjects([])
        })
        .finally(() => {
          if (!cancelled && request === latest) setLoading(false)
        })
    }

    read()

    function onConnectedMessage(event: MessageEvent) {
      // Origin-checked: this listener is open to every frame on the page, and
      // the only sender it will ever hear from is this app's own approval
      // window. A refresh is cheap, but it is still this app's to trigger.
      if (event.origin !== window.location.origin) return
      if (event.data !== BACKLOG_CONNECTED_MESSAGE) return
      read()
    }

    window.addEventListener('focus', read)
    window.addEventListener('message', onConnectedMessage)
    return () => {
      cancelled = true
      window.removeEventListener('focus', read)
      window.removeEventListener('message', onConnectedMessage)
    }
  }, [enabled, reloads])

  return { need, projects, loading, refresh }
}
